/**
 * Firestore access layer.
 *
 * Every read is a live `onSnapshot` subscription, which is the whole point of
 * putting this app on Firestore: when one parent logs a feed, it appears on the
 * other's phone without a refresh. The prototype's local component state maps
 * onto these collections one-for-one.
 *
 * Mutations that should also show up in the "who did what" feed write the entry
 * and its activity row in a single `writeBatch`, so the feed can never
 * disagree with the data it describes.
 */

import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import type {
  CollectionReference,
  DocumentData,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import type { User } from 'firebase/auth';

import { db, storage } from './firebase';
import { canCreateFamily } from './access';
import type {
  Activity,
  ActivityKind,
  Child,
  Entry,
  Family,
  FamilyCodes,
  FeedSource,
  GrowthEntry,
  GrowthKind,
  Member,
  MemberRole,
  Milestone,
  Note,
  ParentRole,
  Photo,
  PrepDay,
  PrepItem,
  UserDoc,
} from './types';
import { addDays, dayKey, startOfDay } from './time';
import { entryActivityAction, entryDeleteAction, entryEditAction, entryTypeIcon } from './labels';
import { DEFAULT_HANDOVER, DEFAULT_PREP_ITEMS, PHOTO_JPEG_QUALITY, PHOTO_MAX_EDGE } from '../data/defaults';

// ---------------------------------------------------------------- plumbing

type Snap = QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>;

/** Firestore Timestamp → Date. Pending server timestamps read back as null. */
function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

/** For fields that must be a Date: falls back to now while a write is in flight. */
function toDateOr(value: unknown, fallback: Date): Date {
  return toDate(value) ?? fallback;
}

export function familyRef(familyId: string) {
  return doc(db, 'families', familyId);
}

function sub(familyId: string, name: string): CollectionReference<DocumentData> {
  return collection(db, 'families', familyId, name);
}

/** Parents-only document holding the invite and share codes. */
export const codesRef = (f: string) => doc(db, 'families', f, 'secrets', 'codes');

export const membersCol = (f: string) => sub(f, 'members');
export const childrenCol = (f: string) => sub(f, 'children');
export const entriesCol = (f: string) => sub(f, 'entries');
export const notesCol = (f: string) => sub(f, 'notes');
export const activityCol = (f: string) => sub(f, 'activity');
export const prepItemsCol = (f: string) => sub(f, 'prepItems');
export const prepDaysCol = (f: string) => sub(f, 'prepDays');
export const growthCol = (f: string) => sub(f, 'growth');
export const milestonesCol = (f: string) => sub(f, 'milestones');
export const photosCol = (f: string) => sub(f, 'photos');

/** Unambiguous share/invite codes — no O/0, I/1, or similar look-alikes. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCode(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

// ---------------------------------------------------------------- parsers

function parseFamily(snap: Snap): Family {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    name: (d.name as string) ?? '',
    onDuty: (d.onDuty as ParentRole | null) ?? null,
    handoverAt: (d.handoverAt as string | null) ?? null,
    parentJoinOpen: d.parentJoinOpen !== false,
    shareEnabled: d.shareEnabled === true,
    showMilestones: d.showMilestones !== false,
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

function parseMember(snap: Snap): Member {
  const d = snap.data() ?? {};
  return {
    uid: snap.id,
    role: (d.role as MemberRole) ?? 'viewer',
    name: (d.name as string) ?? '',
    nameEn: (d.nameEn as string) ?? '',
    initial: (d.initial as string) ?? '?',
    photoURL: (d.photoURL as string | null) ?? null,
    joinedAt: toDate(d.joinedAt),
  };
}

function parseChild(snap: Snap): Child {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    name: (d.name as string) ?? '',
    nameEn: (d.nameEn as string) ?? '',
    kind: d.kind === 'kid' ? 'kid' : 'baby',
    sex: (d.sex as Child['sex']) ?? 'unspecified',
    birthDate: (d.birthDate as string) ?? '',
    order: (d.order as number) ?? 0,
  };
}

function parseEntry(snap: Snap): Entry {
  const d = snap.data() ?? {};
  const now = new Date();
  const base = {
    id: snap.id,
    childId: (d.childId as string) ?? '',
    byUid: (d.byUid as string) ?? '',
    byRole: (d.byRole as ParentRole) ?? 'mom',
    at: toDateOr(d.at, now),
    createdAt: toDate(d.createdAt),
  };

  switch (d.type) {
    case 'diaper':
      return { ...base, type: 'diaper', pee: d.pee === true, poop: d.poop === true };
    case 'sleep':
      return { ...base, type: 'sleep', endAt: toDate(d.endAt) };
    case 'solid':
      return { ...base, type: 'solid', grams: (d.grams as number) ?? 0 };
    case 'feed':
    default:
      return {
        ...base,
        type: 'feed',
        source: (d.source as FeedSource) ?? 'right',
        amount: (d.amount as number) ?? 0,
      };
  }
}

function parseNote(snap: Snap): Note {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    text: (d.text as string) ?? '',
    byUid: (d.byUid as string) ?? '',
    byRole: (d.byRole as ParentRole) ?? 'mom',
    createdAt: toDate(d.createdAt),
  };
}

function parseActivity(snap: Snap): Activity {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    kind: (d.kind as ActivityKind) ?? 'entry',
    action: (d.action as string) ?? '',
    icon: (d.icon as string) ?? 'Baby',
    byUid: (d.byUid as string) ?? '',
    byRole: (d.byRole as ParentRole) ?? 'mom',
    at: toDateOr(d.at, new Date()),
    refId: (d.refId as string | null) ?? null,
  };
}

function parsePrepItem(snap: Snap): PrepItem {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    childId: (d.childId as string) ?? '',
    icon: (d.icon as string) ?? 'Backpack',
    label: (d.label as string) ?? '',
    labelEn: (d.labelEn as string) ?? '',
    note: (d.note as string | null) ?? null,
    order: (d.order as number) ?? 0,
    archived: d.archived === true,
  };
}

function parsePrepDay(snap: Snap): PrepDay {
  const d = snap.data() ?? {};
  const raw = (d.checks as Record<string, DocumentData> | undefined) ?? {};
  const checks: PrepDay['checks'] = {};
  for (const [itemId, value] of Object.entries(raw)) {
    checks[itemId] = {
      byUid: (value.byUid as string) ?? '',
      byRole: (value.byRole as ParentRole) ?? 'mom',
      at: toDateOr(value.at, new Date()),
    };
  }
  return { id: snap.id, checks };
}

function parseGrowth(snap: Snap): GrowthEntry {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    childId: (d.childId as string) ?? '',
    kind: (d.kind as GrowthKind) ?? 'weight',
    value: (d.value as number) ?? 0,
    measuredAt: toDateOr(d.measuredAt, new Date()),
    byUid: (d.byUid as string) ?? '',
    byRole: (d.byRole as ParentRole) ?? 'mom',
  };
}

function parseMilestone(snap: Snap): Milestone {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    childId: (d.childId as string) ?? '',
    icon: (d.icon as string) ?? 'Smile',
    jp: (d.jp as string) ?? '',
    en: (d.en as string) ?? '',
    date: (d.date as string) ?? '',
  };
}

function parsePhoto(snap: Snap): Photo {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    childId: (d.childId as string | null) ?? null,
    storagePath: (d.storagePath as string) ?? '',
    url: (d.url as string) ?? '',
    caption: (d.caption as string | null) ?? null,
    takenAt: toDateOr(d.takenAt, new Date()),
    byUid: (d.byUid as string) ?? '',
    createdAt: toDate(d.createdAt),
  };
}

// ---------------------------------------------------------------- watchers

export type Unsubscribe = () => void;
type Sink<T> = (value: T) => void;
type ErrorSink = (error: Error) => void;

export function watchUserDoc(uid: string, onValue: Sink<UserDoc | null>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      if (!snap.exists()) return onValue(null);
      const d = snap.data();
      onValue({
        uid,
        familyIds: (d.familyIds as string[]) ?? [],
        lastFamilyId: (d.lastFamilyId as string | null) ?? null,
      });
    },
    onError,
  );
}

export function watchFamily(familyId: string, onValue: Sink<Family | null>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    familyRef(familyId),
    (snap) => onValue(snap.exists() ? parseFamily(snap) : null),
    onError,
  );
}

/** Parents only — a viewer's read of this document is denied by the rules. */
export function watchFamilyCodes(
  familyId: string,
  onValue: Sink<FamilyCodes | null>,
  onError?: ErrorSink,
): Unsubscribe {
  return onSnapshot(
    codesRef(familyId),
    (snap) => {
      const d = snap.data();
      onValue(
        d ? { inviteCode: (d.inviteCode as string) ?? '', shareCode: (d.shareCode as string) ?? '' } : null,
      );
    },
    onError,
  );
}

export function watchMembers(familyId: string, onValue: Sink<Member[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(membersCol(familyId), (s) => onValue(s.docs.map(parseMember)), onError);
}

export function watchChildren(familyId: string, onValue: Sink<Child[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(childrenCol(familyId), orderBy('order')),
    (s) => onValue(s.docs.map(parseChild)),
    onError,
  );
}

/**
 * Entries from `since` onwards, newest first.
 *
 * `since` is normally the start of today, but a nap that began yesterday
 * evening and has not been closed still needs to be reachable, so callers pass
 * a slightly wider window and filter for display.
 */
export function watchEntries(
  familyId: string,
  since: Date,
  onValue: Sink<Entry[]>,
  onError?: ErrorSink,
): Unsubscribe {
  return onSnapshot(
    query(entriesCol(familyId), where('at', '>=', Timestamp.fromDate(since)), orderBy('at', 'desc')),
    (s) => onValue(s.docs.map(parseEntry)),
    onError,
  );
}

/**
 * Entries around a single past day, for browsing history in the log.
 *
 * The window reaches back to the previous day's start so an overnight sleep
 * that began the evening before still shows on the day it ended — the same
 * rule entryTouchesDay applies when the screen filters these results. The
 * range is over `at` alone, so no composite index is needed.
 */
export function watchEntriesForDay(
  familyId: string,
  day: Date,
  onValue: Sink<Entry[]>,
  onError?: ErrorSink,
): Unsubscribe {
  const from = startOfDay(addDays(day, -1));
  const to = startOfDay(addDays(day, 1));
  return onSnapshot(
    query(
      entriesCol(familyId),
      where('at', '>=', Timestamp.fromDate(from)),
      where('at', '<', Timestamp.fromDate(to)),
      orderBy('at', 'desc'),
    ),
    (s) => onValue(s.docs.map(parseEntry)),
    onError,
  );
}

/** Naps with no end yet — 記録中. Kept separate so an old open nap is never lost. */
export function watchOpenSleeps(familyId: string, onValue: Sink<Entry[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(entriesCol(familyId), where('type', '==', 'sleep'), where('endAt', '==', null), orderBy('at', 'desc'), limit(10)),
    (s) => onValue(s.docs.map(parseEntry)),
    onError,
  );
}

export function watchNotes(familyId: string, onValue: Sink<Note[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(notesCol(familyId), orderBy('createdAt', 'desc'), limit(30)),
    (s) => onValue(s.docs.map(parseNote)),
    onError,
  );
}

export function watchActivity(familyId: string, onValue: Sink<Activity[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(activityCol(familyId), orderBy('at', 'desc'), limit(40)),
    (s) => onValue(s.docs.map(parseActivity)),
    onError,
  );
}

export function watchPrepItems(familyId: string, onValue: Sink<PrepItem[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(prepItemsCol(familyId), orderBy('order')),
    (s) => onValue(s.docs.map(parsePrepItem).filter((p) => !p.archived)),
    onError,
  );
}

export function watchPrepDay(
  familyId: string,
  day: string,
  onValue: Sink<PrepDay>,
  onError?: ErrorSink,
): Unsubscribe {
  return onSnapshot(
    doc(prepDaysCol(familyId), day),
    (snap) => onValue(snap.exists() ? parsePrepDay(snap) : { id: day, checks: {} }),
    onError,
  );
}

export function watchGrowth(familyId: string, onValue: Sink<GrowthEntry[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(growthCol(familyId), orderBy('measuredAt', 'desc')),
    (s) => onValue(s.docs.map(parseGrowth)),
    onError,
  );
}

export function watchMilestones(familyId: string, onValue: Sink<Milestone[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(milestonesCol(familyId), orderBy('date', 'desc')),
    (s) => onValue(s.docs.map(parseMilestone)),
    onError,
  );
}

export function watchPhotos(familyId: string, onValue: Sink<Photo[]>, onError?: ErrorSink): Unsubscribe {
  return onSnapshot(
    query(photosCol(familyId), orderBy('takenAt', 'desc'), limit(120)),
    (s) => onValue(s.docs.map(parsePhoto)),
    onError,
  );
}

// ---------------------------------------------------------------- activity

type ActivityInput = {
  kind: ActivityKind;
  action: string;
  icon: string;
  by: Member;
  at?: Date;
  refId?: string | null;
};

function activityPayload({ kind, action, icon, by, at, refId }: ActivityInput) {
  return {
    kind,
    action,
    icon,
    byUid: by.uid,
    byRole: by.role,
    at: Timestamp.fromDate(at ?? new Date()),
    refId: refId ?? null,
  };
}

/** Parents only — a `viewer` has nothing to log. */
function asParent(member: Member): ParentRole {
  return member.role === 'dad' ? 'dad' : 'mom';
}

// ---------------------------------------------------------------- setup

export type CreateFamilyInput = {
  familyName: string;
  /** The signing-in parent. */
  role: ParentRole;
  name: string;
  nameEn: string;
  initial: string;
  children: {
    name: string;
    nameEn: string;
    kind: Child['kind'];
    sex: Child['sex'];
    birthDate: string;
  }[];
};

/**
 * Create a family, its first member, its children, and the seed prep list.
 *
 * Also writes the `invites` and `shares` lookup documents, which is what lets
 * the co-parent and the grandparents join by code without being able to
 * enumerate families.
 */
export async function createFamily(user: User, input: CreateFamilyInput): Promise<string> {
  if (!canCreateFamily(user)) throw new Error('このアカウントでは家族を作成できません。招待コードで参加してください。');
  const fRef = doc(collection(db, 'families'));
  const familyId = fRef.id;
  const inviteCode = randomCode(6);
  const shareCode = randomCode(16);

  const batch = writeBatch(db);

  batch.set(fRef, {
    name: input.familyName,
    onDuty: input.role,
    handoverAt: DEFAULT_HANDOVER,
    parentJoinOpen: true,
    shareEnabled: false,
    showMilestones: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  batch.set(codesRef(familyId), { inviteCode, shareCode });

  batch.set(doc(membersCol(familyId), user.uid), {
    role: input.role,
    name: input.name,
    nameEn: input.nameEn,
    initial: input.initial,
    photoURL: user.photoURL ?? null,
    joinedAt: serverTimestamp(),
  });

  batch.set(doc(db, 'invites', inviteCode), { familyId, createdAt: serverTimestamp() });
  batch.set(doc(db, 'shares', shareCode), { familyId, createdAt: serverTimestamp() });

  const kidIds: string[] = [];
  input.children.forEach((child, i) => {
    const cRef = doc(childrenCol(familyId));
    batch.set(cRef, { ...child, order: i });
    if (child.kind === 'kid') kidIds.push(cRef.id);
  });

  // Seed the kindergarten checklist against the eldest, if there is one.
  const prepChildId = kidIds[0] ?? '';
  DEFAULT_PREP_ITEMS.forEach((item, i) => {
    batch.set(doc(prepItemsCol(familyId)), { ...item, childId: prepChildId, order: i, archived: false });
  });

  batch.set(
    doc(db, 'users', user.uid),
    { familyIds: [familyId], lastFamilyId: familyId },
    { merge: true },
  );

  await batch.commit();
  return familyId;
}

export type JoinResult = { familyId: string; role: MemberRole };

/** Look up an invite code and join as the other parent. */
export async function joinFamilyByInvite(
  user: User,
  code: string,
  profile: { role: ParentRole; name: string; nameEn: string; initial: string },
): Promise<JoinResult> {
  const normalized = code.trim().toUpperCase();
  const inviteSnap = await getDoc(doc(db, 'invites', normalized));
  if (!inviteSnap.exists()) throw new Error('この招待コードは見つかりませんでした。');

  const familyId = inviteSnap.data().familyId as string;

  const batch = writeBatch(db);
  batch.set(doc(membersCol(familyId), user.uid), {
    role: profile.role,
    name: profile.name,
    nameEn: profile.nameEn,
    initial: profile.initial,
    photoURL: user.photoURL ?? null,
    joinedAt: serverTimestamp(),
  });
  batch.set(
    doc(db, 'users', user.uid),
    { familyIds: [familyId], lastFamilyId: familyId, joinCode: normalized },
    { merge: true },
  );
  await batch.commit();

  // Both parents are in — close the window rather than leaving the code live.
  const members = await getDocs(membersCol(familyId));
  const parents = members.docs.map(parseMember).filter((m) => m.role !== 'viewer');
  if (parents.length >= 2) {
    await updateDoc(familyRef(familyId), { parentJoinOpen: false, updatedAt: serverTimestamp() });
  }

  return { familyId, role: profile.role };
}

/** Join via the grandparents' share link: read-only, album access only. */
export async function joinFamilyByShare(
  user: User,
  shareCode: string,
  profile: { name: string; nameEn: string; initial: string },
): Promise<JoinResult> {
  const shareSnap = await getDoc(doc(db, 'shares', shareCode.trim()));
  if (!shareSnap.exists()) throw new Error('この共有リンクは無効です。');

  const familyId = shareSnap.data().familyId as string;

  const batch = writeBatch(db);
  batch.set(doc(membersCol(familyId), user.uid), {
    role: 'viewer',
    name: profile.name,
    nameEn: profile.nameEn,
    initial: profile.initial,
    photoURL: user.photoURL ?? null,
    joinedAt: serverTimestamp(),
  });
  batch.set(
    doc(db, 'users', user.uid),
    { familyIds: [familyId], lastFamilyId: familyId, joinCode: shareCode.trim() },
    { merge: true },
  );
  await batch.commit();

  return { familyId, role: 'viewer' };
}

export async function setLastFamily(uid: string, familyId: string): Promise<void> {
  await setDoc(doc(db, 'users', uid), { lastFamilyId: familyId }, { merge: true });
}

// ---------------------------------------------------------------- family settings

export async function setOnDuty(familyId: string, role: ParentRole, by: Member): Promise<void> {
  const batch = writeBatch(db);
  batch.update(familyRef(familyId), { onDuty: role, updatedAt: serverTimestamp() });
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'note',
      action: `担当を${role === 'mom' ? 'ママ' : 'パパ'}に交代`,
      icon: 'RefreshCw',
      by,
    }),
  );
  await batch.commit();
}

export async function setHandoverAt(familyId: string, hhmm: string): Promise<void> {
  await updateDoc(familyRef(familyId), { handoverAt: hhmm, updatedAt: serverTimestamp() });
}

export async function setShowMilestones(familyId: string, value: boolean): Promise<void> {
  await updateDoc(familyRef(familyId), { showMilestones: value, updatedAt: serverTimestamp() });
}

export async function setShareEnabled(familyId: string, value: boolean): Promise<void> {
  await updateDoc(familyRef(familyId), { shareEnabled: value, updatedAt: serverTimestamp() });
}

export async function setParentJoinOpen(familyId: string, value: boolean): Promise<void> {
  await updateDoc(familyRef(familyId), { parentJoinOpen: value, updatedAt: serverTimestamp() });
}

export async function updateMemberProfile(
  familyId: string,
  uid: string,
  patch: Partial<Pick<Member, 'name' | 'nameEn' | 'initial'>>,
): Promise<void> {
  await updateDoc(doc(membersCol(familyId), uid), patch);
}

// ---------------------------------------------------------------- children

export async function addChild(familyId: string, child: Omit<Child, 'id'>): Promise<string> {
  const ref = await addDoc(childrenCol(familyId), child);
  return ref.id;
}

export async function updateChild(
  familyId: string,
  childId: string,
  patch: Partial<Omit<Child, 'id'>>,
): Promise<void> {
  await updateDoc(doc(childrenCol(familyId), childId), patch);
}

// ---------------------------------------------------------------- log entries

export type NewEntry =
  | { type: 'feed'; childId: string; at: Date; source: FeedSource; amount: number }
  | { type: 'diaper'; childId: string; at: Date; pee: boolean; poop: boolean }
  | { type: 'sleep'; childId: string; at: Date; endAt: Date | null }
  | { type: 'solid'; childId: string; at: Date; grams: number };

/** Save a log entry and its activity row together. */
export async function addEntry(familyId: string, by: Member, input: NewEntry): Promise<string> {
  const role = asParent(by);
  const eRef = doc(entriesCol(familyId));

  const common = {
    childId: input.childId,
    byUid: by.uid,
    byRole: role,
    at: Timestamp.fromDate(input.at),
    createdAt: serverTimestamp(),
  };

  const payload =
    input.type === 'feed'
      ? { ...common, type: 'feed', source: input.source, amount: input.amount }
      : input.type === 'diaper'
        ? { ...common, type: 'diaper', pee: input.pee, poop: input.poop }
        : input.type === 'sleep'
          ? { ...common, type: 'sleep', endAt: input.endAt ? Timestamp.fromDate(input.endAt) : null }
          : { ...common, type: 'solid', grams: input.grams };

  const batch = writeBatch(db);
  batch.set(eRef, payload);
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'entry',
      action: entryActivityAction(input),
      icon: entryTypeIcon(input.type),
      by: { ...by, role },
      at: input.at,
      refId: eRef.id,
    }),
  );
  await batch.commit();

  return eRef.id;
}

/**
 * Close an open nap.
 *
 * This is the "if you forget to add the end, you can do it later" path — you
 * can fall asleep alongside them, log nothing, and fill the end in whenever
 * you surface.
 */
export async function closeSleep(familyId: string, entryId: string, endAt: Date, by: Member): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(entriesCol(familyId), entryId), { endAt: Timestamp.fromDate(endAt) });
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'entry',
      action: `ねんね · おわりを記録`,
      icon: 'Moon',
      by: { ...by, role: asParent(by) },
      refId: entryId,
    }),
  );
  await batch.commit();
}

/**
 * Apply a correction and record it on the feed in one batch.
 *
 * Only the editable fields change: the entry keeps its original author,
 * type, child and createdAt — the feed row carries who made the correction.
 */
export async function editEntry(
  familyId: string,
  entryId: string,
  by: Member,
  input: NewEntry,
): Promise<void> {
  const at = Timestamp.fromDate(input.at);
  const patch =
    input.type === 'feed'
      ? { at, source: input.source, amount: input.amount }
      : input.type === 'diaper'
        ? { at, pee: input.pee, poop: input.poop }
        : input.type === 'sleep'
          ? { at, endAt: input.endAt ? Timestamp.fromDate(input.endAt) : null }
          : { at, grams: input.grams };

  const batch = writeBatch(db);
  batch.update(doc(entriesCol(familyId), entryId), patch);
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'entry',
      action: entryEditAction(input),
      icon: 'Pencil',
      by: { ...by, role: asParent(by) },
      refId: entryId,
    }),
  );
  await batch.commit();
}

/** Remove an entry, leaving a feed row that says what went. */
export async function removeEntry(familyId: string, entry: Entry, by: Member): Promise<void> {
  const batch = writeBatch(db);
  batch.delete(doc(entriesCol(familyId), entry.id));
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'entry',
      action: entryDeleteAction(entry),
      icon: 'Trash2',
      by: { ...by, role: asParent(by) },
      refId: null,
    }),
  );
  await batch.commit();
}

// ---------------------------------------------------------------- notes

export async function addNote(familyId: string, by: Member, text: string): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;
  await addDoc(notesCol(familyId), {
    text: trimmed,
    byUid: by.uid,
    byRole: asParent(by),
    createdAt: serverTimestamp(),
  });
}

export async function deleteNote(familyId: string, noteId: string): Promise<void> {
  await deleteDoc(doc(notesCol(familyId), noteId));
}

// ---------------------------------------------------------------- prep

/**
 * Tick or untick a prep item for a given day.
 *
 * Completion is stored per-day, keyed by item id, so tomorrow's list starts
 * empty without touching the item definitions.
 */
export async function setPrepChecked(
  familyId: string,
  day: string,
  item: PrepItem,
  by: Member,
  checked: boolean,
): Promise<void> {
  const role = asParent(by);
  const dayRef = doc(prepDaysCol(familyId), day);

  const batch = writeBatch(db);
  batch.set(
    dayRef,
    {
      checks: checked
        ? { [item.id]: { byUid: by.uid, byRole: role, at: Timestamp.fromDate(new Date()) } }
        : { [item.id]: deleteField() },
    },
    { merge: true },
  );

  if (checked) {
    batch.set(
      doc(activityCol(familyId)),
      activityPayload({
        kind: 'prep',
        action: `${item.label}を準備`,
        icon: item.icon,
        by: { ...by, role },
        refId: item.id,
      }),
    );
  }

  await batch.commit();
}

export async function addPrepItem(
  familyId: string,
  by: Member,
  item: Omit<PrepItem, 'id' | 'archived'>,
): Promise<void> {
  const role = asParent(by);
  const ref = doc(prepItemsCol(familyId));

  const batch = writeBatch(db);
  batch.set(ref, { ...item, archived: false });
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'prep',
      action: `${item.label}を準備リストに追加`,
      icon: item.icon,
      by: { ...by, role },
      refId: ref.id,
    }),
  );
  await batch.commit();
}

/** Archive rather than delete, so past days keep their history. */
export async function archivePrepItem(familyId: string, itemId: string): Promise<void> {
  await updateDoc(doc(prepItemsCol(familyId), itemId), { archived: true });
}

// ---------------------------------------------------------------- growth

export async function addGrowth(
  familyId: string,
  by: Member,
  input: { childId: string; kind: GrowthKind; value: number; measuredAt: Date },
): Promise<void> {
  const role = asParent(by);
  const ref = doc(growthCol(familyId));
  const unit = input.kind === 'weight' ? 'kg' : 'cm';
  const what = input.kind === 'weight' ? '体重' : '身長';

  const batch = writeBatch(db);
  batch.set(ref, {
    childId: input.childId,
    kind: input.kind,
    value: input.value,
    measuredAt: Timestamp.fromDate(input.measuredAt),
    byUid: by.uid,
    byRole: role,
  });
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'growth',
      action: `${what} ${input.value}${unit} を記録`,
      icon: 'TrendingUp',
      by: { ...by, role },
      at: input.measuredAt,
      refId: ref.id,
    }),
  );
  await batch.commit();
}

export async function deleteGrowth(familyId: string, id: string): Promise<void> {
  await deleteDoc(doc(growthCol(familyId), id));
}

export async function addMilestone(
  familyId: string,
  by: Member,
  input: Omit<Milestone, 'id'>,
): Promise<void> {
  const role = asParent(by);
  const ref = doc(milestonesCol(familyId));

  const batch = writeBatch(db);
  batch.set(ref, input);
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'milestone',
      action: `${input.jp} を記録`,
      icon: input.icon,
      by: { ...by, role },
      refId: ref.id,
    }),
  );
  await batch.commit();
}

export async function deleteMilestone(familyId: string, id: string): Promise<void> {
  await deleteDoc(doc(milestonesCol(familyId), id));
}

// ---------------------------------------------------------------- photos

/**
 * Downscale before upload.
 *
 * A modern phone camera produces 4–8 MB files; grandparents scrolling the album
 * on a phone do not need that, and neither does the storage bill. Longest edge
 * is capped and the result re-encoded as JPEG.
 */
async function downscale(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;

  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > PHOTO_MAX_EDGE ? PHOTO_MAX_EDGE / longest : 1;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', PHOTO_JPEG_QUALITY),
  );
  return blob ?? file;
}

export async function addPhoto(
  familyId: string,
  by: Member,
  file: File,
  options: { childId?: string | null; caption?: string | null; takenAt?: Date } = {},
): Promise<void> {
  const blob = await downscale(file);
  const id = doc(photosCol(familyId)).id;
  const path = `families/${familyId}/photos/${id}.jpg`;
  const objectRef = storageRef(storage, path);

  await uploadBytes(objectRef, blob, { contentType: 'image/jpeg' });
  const url = await getDownloadURL(objectRef);

  const takenAt = options.takenAt ?? new Date(file.lastModified || Date.now());

  const batch = writeBatch(db);
  batch.set(doc(photosCol(familyId), id), {
    childId: options.childId ?? null,
    storagePath: path,
    url,
    caption: options.caption ?? null,
    takenAt: Timestamp.fromDate(takenAt),
    byUid: by.uid,
    createdAt: serverTimestamp(),
  });
  batch.set(
    doc(activityCol(familyId)),
    activityPayload({
      kind: 'photo',
      action: '写真を追加',
      icon: 'Image',
      by: { ...by, role: asParent(by) },
      refId: id,
    }),
  );
  await batch.commit();
}

export async function deletePhoto(familyId: string, photo: Photo): Promise<void> {
  await deleteDoc(doc(photosCol(familyId), photo.id));
  // The document is the source of truth for the album; a failed object delete
  // must not resurrect the photo, so it is best-effort.
  await deleteObject(storageRef(storage, photo.storagePath)).catch(() => undefined);
}

// ---------------------------------------------------------------- windows

/**
 * How far back the log subscription reaches: the start of yesterday, so an
 * overnight nap logged before midnight is still available to close today.
 */
export function logWindowStart(now: Date = new Date()): Date {
  const start = startOfDay(now);
  start.setDate(start.getDate() - 1);
  return start;
}

export { dayKey };
